/**
 * @module features/devices/types
 * @description Zod schemas + TypeScript contracts for Device Management. Mirrors
 * specs/002-device-management/contracts/ui-contracts.ts; every API response is
 * validated against these schemas (Constitution Principle XIII).
 */
import { z } from "zod";

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/** Device type ids. Extend alongside the backend registry when new types ship. */
export const DeviceTypeEnum = z.enum(["display_module"]);
export type DeviceType = z.infer<typeof DeviceTypeEnum>;

export const DeviceStatusEnum = z.enum([
  "PROVISIONED",
  "ACTIVE",
  "OFFLINE",
  "MAINTENANCE",
  "DECOMMISSIONED",
]);
export type DeviceStatus = z.infer<typeof DeviceStatusEnum>;

export const DEVICE_STATUSES = DeviceStatusEnum.options;

// ---------------------------------------------------------------------------
// Entities
// ---------------------------------------------------------------------------

export const FirmwareHistoryItemSchema = z.object({
  id: z.number(),
  previous_os_version: z.string(),
  previous_os_hash: z.string(),
  new_os_version: z.string(),
  new_os_hash: z.string(),
  notes: z.string().nullable().optional(),
  created_at: z.string(),
  created_by_name: z.string().nullable().optional(),
});
export type FirmwareHistoryItem = z.infer<typeof FirmwareHistoryItemSchema>;

export const DisplayModuleDetailsSchema = z.object({
  os_name: z.string().min(1, "OS name is required").max(100),
  os_version: z.string().min(1, "OS version is required").max(50),
  os_hash: z.string().min(1, "OS hash is required").max(128),
});
export type DisplayModuleDetails = z.infer<typeof DisplayModuleDetailsSchema>;

export const DeviceItemSchema = z.object({
  id: z.string(),
  device_name: z.string(),
  mac_address: z.string().nullable().optional(),
  registered_device_id: z.string(),
  device_type: DeviceTypeEnum,
  status: DeviceStatusEnum,
  last_seen_at: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable().optional(),
  created_by_name: z.string().nullable().optional(),
  updated_by_name: z.string().nullable().optional(),
  deleted_by_name: z.string().nullable().optional(),
  display_module: DisplayModuleDetailsSchema.partial().optional(),
  firmware_history: z.array(FirmwareHistoryItemSchema).optional(),
});
export type DeviceItem = z.infer<typeof DeviceItemSchema>;

export const PaginationSchema = z.object({
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
  total_pages: z.number(),
  has_next: z.boolean().optional(),
  has_prev: z.boolean().optional(),
});
export type DevicePagination = z.infer<typeof PaginationSchema>;

export const DeviceListSchema = z.object({
  items: z.array(DeviceItemSchema),
  pagination: PaginationSchema,
});
export type DeviceList = z.infer<typeof DeviceListSchema>;

// ---------------------------------------------------------------------------
// Form schemas
// ---------------------------------------------------------------------------

/** Accepts XX:XX:XX:XX:XX:XX, XX-XX-XX-XX-XX-XX, XXXX.XXXX.XXXX or 12 plain hex digits. */
export const MAC_REGEX =
  /^(?:[0-9A-Fa-f]{2}([:-]))(?:[0-9A-Fa-f]{2}\1){4}[0-9A-Fa-f]{2}$|^(?:[0-9A-Fa-f]{4}\.){2}[0-9A-Fa-f]{4}$|^[0-9A-Fa-f]{12}$/;
export const OS_HASH_REGEX = /^[A-Za-z0-9._:+-]+$/;

export const DeviceCreateFormSchema = z.object({
  device_name: z
    .string()
    .trim()
    .min(1, "Device name is required")
    .max(100, "Device name cannot exceed 100 characters"),
  mac_address: z
    .string()
    .trim()
    .optional(),
  registered_device_id: z
    .string()
    .trim()
    .max(100, "Registration ID cannot exceed 100 characters")
    .optional(),
  device_type: DeviceTypeEnum,
  os_name: z.string().trim().min(1, "OS name is required").max(100),
  os_version: z.string().trim().min(1, "OS version is required").max(50),
  os_hash: z
    .string()
    .trim()
    .min(1, "OS hash is required")
    .max(128, "OS hash cannot exceed 128 characters")
    .regex(OS_HASH_REGEX, "Letters, digits and . _ : + - only"),
});
export type DeviceCreateFormValues = z.infer<typeof DeviceCreateFormSchema>;

export interface DeviceUpdateInput {
  device_name?: string;
  status?: DeviceStatus;
  os_name?: string;
  os_version?: string;
  os_hash?: string;
  notes?: string;
}

// ---------------------------------------------------------------------------
// Bulk import, stats and catalog
// ---------------------------------------------------------------------------

export const BulkImportRowErrorSchema = z.object({
  row: z.number(),
  column: z.string().optional(),
  identifier: z.string(),
  reason: z.string(),
});
export type BulkImportRowError = z.infer<typeof BulkImportRowErrorSchema>;

export const BulkImportReportSchema = z.object({
  total_rows: z.number(),
  successful_rows: z.number(),
  failed_rows: z.number(),
  errors: z.array(BulkImportRowErrorSchema),
});
export type BulkImportReport = z.infer<typeof BulkImportReportSchema>;

export const DeviceStatsSchema = z.object({
  total_devices: z.number(),
  by_status: z.record(z.string(), z.number()),
  by_type: z.record(z.string(), z.number()),
});
export type DeviceStats = z.infer<typeof DeviceStatsSchema>;

export const DeviceTypeFieldSchema = z.object({
  name: z.string(),
  label: z.string(),
  type: z.string(),
  required: z.boolean(),
  placeholder: z.string().optional(),
});

export const DeviceTypeDescriptorSchema = z.object({
  type_id: z.string(),
  label: z.string(),
  mac_address_required: z.boolean().default(true),
  fields: z.array(DeviceTypeFieldSchema),
});
export type DeviceTypeDescriptor = z.infer<typeof DeviceTypeDescriptorSchema>;
export const DeviceTypeListSchema = z.array(DeviceTypeDescriptorSchema);

export interface DevicesQueryParams {
  page?: number;
  page_size?: number;
  search?: string;
  device_type?: string;
  status?: string;
}
